import { useSearch } from '@tanstack/react-router';
import { useEngagementPermissions, useUserRoles } from '@anytag/auth';
import {
  EngagementCampaignSocialAccountPostStatus,
  EngagementDetail,
  useProcessMetricsOcrMutation
} from '@anytag/graphql';
import { Button, ButtonLink, DownloadButton, Form } from '@anytag/shared-atoms';
import { GTM_IDS } from '@anytag/shared-constants';
import { useDownloadFile, useQueryHelper } from '@anytag/shared-hooks';
import { SelectorField, TextSearchField } from '@anytag/shared-molecules';
import { GenericOption } from '@anytag/shared-types';
import { getRestApiUrl } from '@anytag/shared-utils';
import { EngagementPostsFilterFormKeys } from './schemaTypes';

export interface TemplateProps {
  onSubmit?: () => void;
  isOnlyTikTokTtcm?: boolean;
  engagement: EngagementDetail;
}

export const Template = ({
  onSubmit,
  isOnlyTikTokTtcm,
  engagement: { id: engagementId, isTtcmCampaign }
}: TemplateProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const search = useSearch({ from: '/_private-routes/engagement/$id/posts' });
  const { hideAddPostBtn } = useEngagementPermissions();
  const { isAdminStaff, isAdminStaffTalentAgencyPartner } = useUserRoles();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { callProcessMetricsOcr, loading } = useProcessMetricsOcrMutation();

  const statusOptions: GenericOption<EngagementCampaignSocialAccountPostStatus>[] = [
    { label: t<string>('Option.Reviewing'), value: EngagementCampaignSocialAccountPostStatus.REVIEWING },
    { label: t<string>('Option.Ready to Post'), value: EngagementCampaignSocialAccountPostStatus.READY_TO_POST },
    { label: t<string>('Option.Effective'), value: EngagementCampaignSocialAccountPostStatus.EFFECTIVE },
    { label: t<string>('Option.Warning'), value: EngagementCampaignSocialAccountPostStatus.WARNING },
    { label: t<string>('Option.Ineffective'), value: EngagementCampaignSocialAccountPostStatus.INEFFECTIVE },
    { label: t<string>('Option.Approved'), value: EngagementCampaignSocialAccountPostStatus.APPROVED },
    { label: t<string>('Option.Rejected'), value: EngagementCampaignSocialAccountPostStatus.REJECT }
  ];

  const hasAddPostBtn = !hideAddPostBtn && isAdminStaffTalentAgencyPartner && !isOnlyTikTokTtcm;

  const handleProcessMetricsOcr = async () => {
    try {
      await callProcessMetricsOcr({ variables: { input: { engagementCampaignId: engagementId } } });
      enqueueSnackbar(t('Message.OcrProcessInitiated'), { variant: 'success' });
    } catch (error) {
      console.error(t(error.message), { variant: 'error' });
    }
  };

  return (
    <Form.Fields>
      <TextSearchField<EngagementPostsFilterFormKeys>
        name="keyword"
        onSubmit={onSubmit}
        placeholder={t<string>('Search.EngagementPostFilter')}
      />

      <SelectorField<EngagementPostsFilterFormKeys>
        name="status"
        onSubmit={onSubmit}
        options={statusOptions}
        className="max-w-47"
      />

      {hasAddPostBtn ? (
        <Form.Field className="flex-none">
          <ButtonLink
            variant="solid"
            className="min-w-24"
            prefixIcon={{ icon: 'plus', size: '10px' }}
            title={t(isTtcmCampaign ? 'Button.Add Video' : 'Button.Add Post')}
            to="/engagement/$id/posts/add"
            search={search}
          />
        </Form.Field>
      ) : null}
      {isAdminStaff ? (
        <Form.Field className="flex-none">
          <Button
            prefixIcon={{ icon: 'database', size: '16px', variant: 'gray' }}
            title={t('Button.Data Extraction')}
            onClick={handleProcessMetricsOcr}
            disabled={loading}
            className="px-2"
            loading={{
              showIcon: true,
              status: loading
            }}
          />
        </Form.Field>
      ) : null}
      <Form.Field className="flex-none">
        <DownloadButton
          disabled={downloading}
          id={GTM_IDS.engagement.posts_csv_download}
          onClick={() =>
            handleDownloadFile({
              filenamePrefix: 'post_comments',
              url: getRestApiUrl(`engagement/${engagementId}/posts/comments/`)
            })
          }
        />
      </Form.Field>
    </Form.Fields>
  );
};
